import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, PenLine, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Reveal, EASE } from "../motion";
import { useLang } from "../../lib/i18n";

const Stars = ({ n = 5 }) => (
  <div className="flex gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i < n ? "fill-gold text-gold" : "text-taupe/30"}`} strokeWidth={1} />
    ))}
  </div>
);

const Card = ({ name, service, text, rating, featured, pending, badge, testid }) => (
  <figure
    className={`h-full flex flex-col justify-between rounded-3xl border p-8 lg:p-10 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(44,36,27,0.08)] ${
      pending ? "bg-beige/50 border-dashed border-taupe/40" : featured ? "bg-espresso text-cream border-taupe/20" : "bg-white border-taupe/20"
    }`}
    data-testid={testid}
  >
    <div>
      <div className="flex items-center justify-between gap-3">
        <Stars n={rating} />
        {pending && (
          <span className="text-[10px] tracking-[0.08em] uppercase text-taupe-dark bg-white/70 rounded-full px-3 py-1 border border-taupe/25">
            {badge}
          </span>
        )}
      </div>
      <blockquote className={`mt-6 font-display leading-snug ${featured ? "text-2xl lg:text-3xl italic" : "text-lg lg:text-xl"}`}>
        “{text}”
      </blockquote>
    </div>
    <figcaption className="mt-8 flex items-center gap-4">
      <span className={`w-11 h-11 rounded-full flex items-center justify-center font-display text-lg shrink-0 ${featured && !pending ? "bg-taupe/30 text-gold" : "bg-beige text-taupe-dark"}`}>
        {name.charAt(0).toUpperCase()}
      </span>
      <span>
        <span className={`block text-sm font-medium ${featured && !pending ? "text-cream" : "text-espresso"}`}>{name}</span>
        {service && <span className={`block text-xs mt-0.5 ${featured && !pending ? "text-cream/55" : "text-espresso/50"}`}>{service}</span>}
      </span>
    </figcaption>
  </figure>
);

export const Testimonials = ({ services = [] }) => {
  const { t, tf } = useLang();
  const samples = t("testimonials.items");
  const [approved, setApproved] = useState([]);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", service_name: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);

  const loadMine = () => {
    const ids = JSON.parse(localStorage.getItem("nadou_my_reviews") || "[]");
    if (ids.length) {
      api.get("/reviews/mine", { params: { ids: ids.join(",") } })
        .then((r) => setMine(r.data.reviews.filter((x) => x.status === "pending")))
        .catch(() => {});
    }
  };

  useEffect(() => {
    api.get("/reviews").then((r) => setApproved(r.data.reviews)).catch(() => {});
    loadMine();
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.text.trim()) return toast.error(t("reviews.error"));
    setSending(true);
    try {
      const { data } = await api.post("/reviews", form);
      const ids = JSON.parse(localStorage.getItem("nadou_my_reviews") || "[]");
      localStorage.setItem("nadou_my_reviews", JSON.stringify([...ids, data.id]));
      setOpen(false);
      setForm({ name: "", service_name: "", rating: 5, text: "" });
      toast.success(t("reviews.success"));
      loadMine();
    } catch (e) {
      toast.error(formatApiError(e, t("reviews.error")));
    } finally {
      setSending(false);
    }
  };

  const cards = approved.length
    ? approved.map((r) => ({ key: r.id, name: r.name, service: tf(`svcName.${r.service_name}`, r.service_name), text: r.text, rating: r.rating }))
    : samples.map((s, i) => ({ key: `sample-${i}`, name: s.name, service: tf(`svcName.${s.service}`, s.service), text: s.text, rating: 5 }));

  const inputCls =
    "w-full bg-white border border-taupe/30 rounded-md px-4 py-3.5 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300";

  return (
    <section className="py-28 lg:py-36 bg-cream relative overflow-hidden" data-testid="testimonials-section">
      <Quote className="absolute -top-8 right-8 lg:right-24 w-48 h-48 text-taupe/10 rotate-12 pointer-events-none" strokeWidth={0.5} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-[12px] tracking-[0.28em] uppercase text-taupe-dark mb-4">{t("testimonials.kicker")}</p>
              <h2 className="font-display tracking-tight text-4xl sm:text-5xl lg:text-6xl text-espresso max-w-2xl leading-tight">
                {t("testimonials.titleA")} <span className="italic text-taupe">{t("testimonials.titleB")}</span>
              </h2>
            </div>
            <button
              data-testid="review-open-btn"
              onClick={() => setOpen(!open)}
              className="rounded-full bg-espresso text-cream px-6 py-3.5 text-[12px] tracking-[0.12em] uppercase inline-flex items-center gap-2 hover:bg-taupe hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              {open ? <X className="w-4 h-4" strokeWidth={1.5} /> : <PenLine className="w-4 h-4" strokeWidth={1.5} />}
              {t("reviews.cta")}
            </button>
          </div>
        </Reveal>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="mt-10 bg-white border border-taupe/25 rounded-3xl p-8 lg:p-10" data-testid="review-form">
                <h3 className="font-display text-2xl text-espresso">{t("reviews.formTitle")}</h3>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <input className={inputCls} data-testid="review-name-input" placeholder={t("reviews.namePh")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <select className={inputCls} data-testid="review-service-select" value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })}>
                    <option value="">{t("reviews.servicePh")}</option>
                    {services.map((s) => <option key={s.id} value={s.name}>{tf(`svcName.${s.name}`, s.name)}</option>)}
                  </select>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-xs tracking-[0.14em] uppercase text-espresso/55">{t("reviews.ratingLabel")}</span>
                  <div className="flex gap-1.5" data-testid="review-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} data-testid={`review-star-${n}`} onClick={() => setForm({ ...form, rating: n })} aria-label={`${n}/5`}>
                        <Star className={`w-6 h-6 transition-transform duration-200 hover:scale-110 ${n <= form.rating ? "fill-gold text-gold" : "text-taupe/35"}`} strokeWidth={1.2} />
                      </button>
                    ))}
                  </div>
                </div>
                <textarea className={`${inputCls} mt-4 min-h-[110px]`} data-testid="review-text-input" placeholder={t("reviews.textPh")} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="review-submit-btn"
                    onClick={submit}
                    disabled={sending}
                    className="rounded-full bg-taupe text-cream px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-taupe-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-300 disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t("reviews.submit")}
                  </button>
                  <p className="text-xs text-espresso/45 max-w-xs leading-relaxed">{t("reviews.success")}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-16 grid lg:grid-cols-12 gap-6">
          {cards.map((c, i) => (
            <Reveal key={c.key} delay={0.08 * i} className={i === 0 ? "lg:col-span-6" : "lg:col-span-3"}>
              <Card {...c} featured={i === 0} testid={`testimonial-${i}`} />
            </Reveal>
          ))}
          {mine.map((r, i) => (
            <Reveal key={r.id} delay={0.08 * (cards.length + i)} className="lg:col-span-3">
              <Card name={r.name} service={r.service_name} text={r.text} rating={r.rating} pending badge={t("reviews.pendingBadge")} testid={`my-review-${r.id}`} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
